import { useMemo } from 'react';
import { useForm, Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ZodObject, ZodRawShape } from 'zod';

import { PaymentProviderName } from 'domain/paySettings/helpers';

import { globalAccountsConfig } from '../config';
import { FieldConfig } from '../types';

// Narrow a provider's create schema to only its editable fields (edit mode).
export const buildUpdateSchema = <T extends ZodRawShape>(schema: ZodObject<T>, fields: FieldConfig[]) => {
  const editableNames = fields.filter((field) => field.editable).map((field) => field.name) as (keyof T)[];

  return schema.pick(
    editableNames.reduce((acc, key) => {
      // @ts-ignore
      acc[key] = true;
      return acc;
    }, {}),
  );
};

type Args = {
  providerName: PaymentProviderName;
  selectedAccount?: Record<string, unknown>;
};

// Builds the react-hook-form instance for the add/edit account modal. Each
// provider config may override defaults/resolver via a custom form strategy
// (getDefaultValues / createResolver); otherwise the generic config-driven
// behaviour applies.
const useAccountForm = ({ providerName, selectedAccount }: Args) => {
  const config = globalAccountsConfig[providerName];
  const isEdit = Boolean(selectedAccount);

  const defaultValues = useMemo<Record<string, unknown>>(() => {
    if (config.getDefaultValues) {
      return config.getDefaultValues(selectedAccount);
    }

    const values: Record<string, unknown> = {};

    config.fields.forEach((field) => {
      values[field.name] = (selectedAccount as Record<string, unknown>)?.[field.name] ?? field.defaultValue;
    });

    return values;
  }, [config, selectedAccount]);

  const resolver = useMemo<Resolver<Record<string, unknown>>>(() => {
    if (config.createResolver) {
      return config.createResolver(isEdit);
    }

    const schema = isEdit ? buildUpdateSchema(config.schema, config.fields) : config.schema;

    // zodResolver returns a resolver with generic types that don't map cleanly
    // to our narrower Resolver<Record<string, unknown>> — go through unknown
    // to satisfy the compiler safely.
    return zodResolver(schema) as unknown as Resolver<Record<string, unknown>>;
  }, [config, isEdit]);

  const form = useForm<Record<string, unknown>>({ resolver, mode: 'onChange', defaultValues });

  return { form, isEdit };
};

export default useAccountForm;
